import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BASE_URL } from "../../constants";
import AuthFrame from "./v2/AuthFrame";
import { TitleBlock, Field, PasswordInput, Note, Actions, PrimaryBtn, SecondaryBtn } from "./v2/pieces";
import { sentence } from "./v2/serverText";

// The messages UserPasswordResetConfirmView answers with (400 for a dead
// link or a blank password, 500 when the write failed). They arrive in
// `error`; a `message` wins when the server sends one.
export const RESET_LINK_DEAD = "This reset link is invalid or has expired. Request a new one.";
const RESET_FAILED = "We could not reset your password. Try again.";

// The plain message for a refused reset, and whether the link itself is dead
// (a new one has to be requested).
export const resetFailure = (status, data) => {
  const body = data && typeof data === "object" ? data : {};
  const text = sentence(body.message) || sentence(body.error) || sentence(body.detail);
  if (status === 429) return { message: text || "Too many tries. Wait a few minutes and try again.", linkDead: false };
  if (status >= 500) return { message: text || RESET_FAILED, linkDead: false };
  if (text) return { message: text, linkDead: /\blink\b/i.test(text) };
  return { message: RESET_LINK_DEAD, linkDead: true };
};

// A claim link (&claim=1) opens this page for an athlete whose profile our
// team built and an admin transferred to their email (the Users page
// "Transfer account"): the same form and checks, worded as choosing their
// first password. The link may carry the email (&email=); a value that is
// not an email is left out.
const CLAIM_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const claimEmailFrom = (value) => {
  const email = typeof value === "string" ? value.trim() : "";
  return CLAIM_EMAIL.test(email) ? email : "";
};
export const CLAIM_DONE = "Your password is set. Log in with your email and new password.";

// Reset password, on the V2 auth frame: the page the emailed link opens.
const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [linkDead, setLinkDead] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const uid = searchParams.get("uid");
  const token = searchParams.get("token");
  const claim = searchParams.get("claim") === "1";
  const claimEmail = claim ? claimEmailFrom(searchParams.get("email")) : "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLinkDead(false);
    if (!uid || !token) {
      setError(RESET_LINK_DEAD);
      setLinkDead(true);
      return;
    }
    if (!password.trim()) {
      setError("Enter a new password.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/users/password-reset/confirm/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, token, password }),
      });
      let data = {};
      try { data = await response.json(); } catch { data = {}; }
      if (response.ok) {
        toast.success(claim ? CLAIM_DONE : "Your password is reset. Log in with the new one.");
        navigate("/login");
      } else {
        const failure = resetFailure(response.status, data);
        setError(failure.message);
        setLinkDead(failure.linkDead);
      }
    } catch {
      setError(RESET_FAILED);
    }
    setLoading(false);
  };

  return (
    <AuthFrame title={claim ? "Claim your profile" : "Reset your password"} onBack={() => navigate("/login")}>
      <form className="contents" onSubmit={handleSubmit}>
        {claim ? (
          <TitleBlock eyebrow="Password" title="Set your password" sub={`Welcome to CACHE. Choose a password for ${claimEmail || "your account"}.`} />
        ) : (
          <TitleBlock eyebrow="Password" title="Set a new password" sub="Choose the password you will log in with from now on." />
        )}
        <Field label="New password">
          <PasswordInput autoComplete="new-password" placeholder="Enter new password" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="New password" />
        </Field>
        <Field label="Confirm new password">
          <PasswordInput autoComplete="new-password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} aria-label="Confirm new password" />
        </Field>
        {error && <Note tone="critical">{error}</Note>}
        <Actions>
          <PrimaryBtn type="submit" loading={loading}>{claim ? "Set password" : "Reset password"}</PrimaryBtn>
          {linkDead && <SecondaryBtn onClick={() => navigate("/forgot-password")}>Request a new link</SecondaryBtn>}
        </Actions>
      </form>
    </AuthFrame>
  );
};

export default ResetPassword;
